import React, { useState } from 'react';
import { FIELD_CLASS, MANAGE } from '../ISRConstants';
import { TFieldClass } from '../Pages/DefineModel/model';
import { ClassIcon, GearIcon } from 'assests/GmsIcons';
import ManageClassDialog from '../Pages/DefineModel/Components/ManageClassDialog';
import { useISRContext } from '../Hooks/useISRContext';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Button from 'Components/DesignSystem/Button/Button';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';

/**
 * The "Field class" picker with its Manage action.
 *
 * **Why this exists.** `IntegerConfiguration`, `DecimalConfiguration` and
 * `FractionConfiguration` each carried the same ~70-line block: a native `<select>`
 * of `isrContext.fieldClasses`, a Manage button, and — duplicated inside each of
 * them again — a Guide-mode arm that disabled both and wrapped each in its own
 * hand-rolled hover tooltip. Six near-identical copies of one control across three
 * files. Fraction's copy had already drifted to a different id scheme
 * (`field-class-*` rather than `class-*`).
 *
 * **What it does NOT own.** Resolving the chosen name back to a `TFieldClass` and
 * writing it into `fieldDataConfiguration` stays with each caller, unchanged — this
 * reports the selected NAME and nothing else. Same rule as `RestrictionsControl`:
 * no state machine moves, so none can drift as a result of the extraction.
 */

const GUIDE_LOCKED_MESSAGE = 'Available with Configure';

export type FieldClassSelectorProps = {
  /** Prefix for this instance's DOM ids and test ids. */
  baseId: string;
  /** The currently selected class NAME (`fieldDataConfiguration.class?.name`). */
  value: string;
  /** Reports the newly selected class name. The caller resolves and stores it. */
  onChange: (className: string) => void;
  /**
   * Guide mode. Passed in rather than derived here so each panel keeps ownership of
   * its own gate — all three currently compute `pathname.includes('/guide')`.
   */
  disabled?: boolean;
  projectId?: string | null;
  className?: string;
};

const FieldClassSelector = ({
  baseId,
  value,
  onChange,
  disabled = false,
  projectId,
  className = '',
}: FieldClassSelectorProps) => {
  const isrContext = useISRContext();
  const [showClassMenu, setShowClassMenu] = useState<boolean>(false);

  const options = [
    { value: '', label: 'Select Class' },
    ...(isrContext?.fieldClasses ?? []).map((item: TFieldClass) => ({
      value: item?.name,
      label: item?.name,
    })),
  ];

  return (
    <div className={className}>
      <div id={`${baseId}-class-header`} className="tw-flex tw-items-center tw-gap-1 tw-mt-4">
        <ClassIcon />
        <FieldTitle label={FIELD_CLASS} data-testid={`${baseId}-class-title`} />
      </div>
      <div id={`${baseId}-class-section`} className="tw-flex tw-items-center tw-gap-4 tw-mt-1 tw-pb-3">
        {/*
          `disabled` on the Tooltip rather than two branches of near-identical
          markup. The wrapper span the Tooltip renders is what receives the hover,
          which is the only reason this works over a DISABLED control — a disabled
          button or dropdown trigger fires no mouse events of its own.
        */}
        <div id={`${baseId}-class-select-container`} className="tw-w-[30%]">
          <Tooltip content={GUIDE_LOCKED_MESSAGE} disabled={!disabled} placement="top" block>
            <Dropdown
              id={`${baseId}-class-select`}
              data-testid={`${baseId}-class-select`}
              label=""
              aria-label={FIELD_CLASS}
              options={options}
              value={value}
              onChange={(next) => onChange(String(next))}
              disabled={disabled}
              usePortal
              className="tw-w-full"
            />
          </Tooltip>
        </div>
        <Tooltip content={GUIDE_LOCKED_MESSAGE} disabled={!disabled} placement="top">
          <Button
            id={`${baseId}-manage-class-btn`}
            data-testid={`${baseId}-manage-class-btn`}
            variant="tertiary"
            size="sm"
            icon={<GearIcon />}
            showIcon
            disabled={disabled}
            onClick={disabled ? undefined : () => setShowClassMenu(true)}
          >
            {MANAGE}
          </Button>
        </Tooltip>
      </div>

      {showClassMenu ? (
        <ManageClassDialog
          id={`${baseId}-class-dialog`}
          showClassMenu={showClassMenu}
          setShowClassMenu={setShowClassMenu}
          projectId={projectId ?? undefined}
        />
      ) : null}
    </div>
  );
};

export default FieldClassSelector;
